import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { MdOutlineAttachFile, MdOutlineRemoveRedEye } from 'react-icons/md';
import Search from 'components/Common/Search';
import Select from 'components/Common/Select';
import { Link, Outlet } from 'react-router-dom';
import { MdOutlineDesktopWindows } from 'react-icons/md';
import { ColumnDef } from '@tanstack/react-table';
import { formatDate, getWebChannelSourceLabel } from 'utils/helperFunctions';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import DateRangePicker from 'components/Common/DateRangePicker';
import { Box, IconButton, Typography, useTheme, Tooltip } from '@mui/material';
import { useGetAgent } from 'apis/business/agent/hook';
import { useArchiveInteractionLog, useGetInteractionsLogs } from 'apis/business/agent/callLogs/hook';
import { FiMessageSquare } from 'react-icons/fi';
import { RiInboxArchiveLine } from 'react-icons/ri';
import ReportFlagIcon from '@/assets/icons/ReportFlagIcon';

interface FilterFormData {
  source_eq: string;
  call_state: 'active' | 'archived' | 'all';
  'filter[calls_received_in]'?: string;
}

const defaultFormValues: FilterFormData = {
  source_eq: 'all',
  call_state: 'active'
};

export default function ChatLogs() {
  const theme = useTheme();
  const { control, handleSubmit, watch } = useForm<FilterFormData>({
    defaultValues: defaultFormValues
  });

  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();
  const [searchText, setSearchText] = useState<null | string>(null);
  const [filterSearchText, setFilterSearchText] = useState<null | string>(null);

  const transformFilters = (formData: FilterFormData) => {
    const filters: Record<string, any> = { ...formData };
    if (filters.source_eq === 'all') {
      delete filters.source_eq;
    } else {
      filters['filter[channel_eq]'] = filters.source_eq;
      delete filters.source_eq;
    }
    if (formData.call_state === 'active') {
      filters['filter[discarded_at_null]'] = true;
    } else if (formData.call_state === 'archived') {
      filters['filter[discarded_at_not_null]'] = true;
    }
    delete filters.call_state;
    return filters;
  };

  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>(transformFilters(defaultFormValues));
  const { mutate: archiveCallLog } = useArchiveInteractionLog();

  const watchedValues = watch();
  useEffect(() => {
    const transformedFilters = transformFilters(watchedValues);
    setAppliedFilters((prevFilters) => {
      const filtersChanged = JSON.stringify(prevFilters) !== JSON.stringify(transformedFilters);
      if (filtersChanged) {
        resetPage();
      }
      return transformedFilters;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watchedValues), resetPage]);

  useEffect(() => {
    setFilterSearchText(searchText);
    resetPage();
  }, [searchText, resetPage]);

  const onSubmit = (data: FilterFormData) => {
    const transformedFilters = transformFilters(data);
    setAppliedFilters(transformedFilters);
    resetPage();
  };

  const { data: callLogs, isLoading: isCallLogsLoading } = useGetInteractionsLogs({
    include: 'contact,agent,business,intent_signal',
    ...appliedFilters,
    'filter[source_info_or_session_id_cont]': filterSearchText,
    'filter[interaction_type_eq]': 1,
    page: currentPage,
    per_page: perPage
  });
  const { data: agent } = useGetAgent();

  return (
    <>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        marginBottom={3}
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={{ xs: 2, lg: 0 }}
        sx={{ width: '100%' }}
      >
        <Typography variant='h6' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
          Chat Logs
        </Typography>
        <Box
          component='form'
          onSubmit={handleSubmit(onSubmit)}
          display='flex'
          gap={{ xs: 1, lg: 1 }}
          flexDirection={{ xs: 'column', lg: 'row' }}
          sx={{ width: { xs: '100%', lg: 'auto' } }}
        >
          <Box sx={{ width: { xs: '100%', lg: '180px' } }}>
            <Select
              name='source_eq'
              label='Source'
              sortOrder='none'
              defaultValue='all'
              control={control}
              options={[
                { value: 'all', label: 'All' },
                { value: 'sms_chat', label: 'SMS' },
                { value: 'web_chat', label: 'Website' },
                { value: 'personalized_link_chat', label: 'Personalized Link' }
              ]}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '180px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '180px' } }}>
            <Select
              name='call_state'
              label='Call State'
              sortOrder='none'
              control={control}
              options={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'archived', label: 'Archived' }
              ]}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '180px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '270px' }, minWidth: { xs: '100%', lg: '270px' } }}>
            <DateRangePicker name='filter[calls_received_in]' control={control} createdAt={agent?.created_at} />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '220px' } }}>
            <Search
              size='small'
              sx={{
                minWidth: { xs: '100%', lg: 220 },
                width: { xs: '100%', lg: 'auto' },
                '& .MuiInputBase-root': {
                  backgroundColor: theme.palette.background.paper
                }
              }}
              searchText={searchText}
              setSearchText={setSearchText}
              placeholder={
                ['phone_call', 'sms_chat', 'all'].includes(watch('source_eq'))
                  ? 'Number / Session ID'
                  : 'Search by Session ID'
              }
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: '100%', overflowX: 'auto', mb: { xs: 2, lg: 0 } }}>
        <DataTable
          data={callLogs?.data}
          columns={columns(archiveCallLog)}
          isHeaderSticky={true}
          isLoading={isCallLogsLoading}
        />
      </Box>
      <Box sx={{ pb: { xs: 2, lg: 0 } }}>
        <Pagination
          totalPages={callLogs?.meta?.total_pages}
          totalCount={callLogs?.meta?.total_count ?? 0}
          currentPage={currentPage}
          perPage={perPage}
          onChange={setPage}
          onPerPageChange={setPerPage}
        />
      </Box>
      <Outlet />
    </>
  );
}

type ArchiveCallLog = (id: number) => void;
const columns = (archiveCallLog: ArchiveCallLog): ColumnDef<any>[] => [
  {
    header: 'SOURCE',
    size: 160,
    cell: (info) => {
      const { channel, source_info, created_at, report_call: report, test } = info?.row?.original || {};
      const getIconLabel = (sourceType: string) => {
        switch (sourceType) {
          case 'personalized_link_chat':
            return { icon: <MdOutlineAttachFile />, label: 'Personalized Link' };
          case 'sms_chat':
            return {
              icon: <FiMessageSquare />,
              label: (typeof source_info === 'string' ? source_info : source_info?.from_phone) || '-'
            };
          case 'phone_chat':
            return { icon: <FiMessageSquare />, label: source_info?.from_phone || '-' };
          default:
            return {
              icon: <MdOutlineDesktopWindows />,
              label: getWebChannelSourceLabel(typeof source_info === 'string' ? source_info : undefined, created_at)
            };
        }
      };
      const { icon, label } = getIconLabel(channel);
      return (
        <Box display='flex' gap={1} alignItems='center' fontWeight={500} sx={{ minWidth: 0 }}>
          <Box mt='3px' sx={{ flexShrink: 0 }}>
            {icon}
          </Box>
          <Typography sx={{ fontSize: { xs: '14px', lg: '16px' }, wordBreak: 'break-word' }}>{label}</Typography>
          {test && (
            <Typography
              component='span'
              sx={{
                fontSize: 11,
                borderRadius: 6,
                px: 1,
                py: 0.25,
                fontWeight: 500,
                color: '#6B7280',
                bgcolor: '#F3F4F6',
                flexShrink: 0
              }}
            >
              Test
            </Typography>
          )}
          {report?.description && (
            <Tooltip title={report.description} arrow placement='top'>
              <Box display='flex' sx={{ flexShrink: 0, lineHeight: 0 }}>
                <ReportFlagIcon />
              </Box>
            </Tooltip>
          )}
        </Box>
      );
    }
  },
  {
    header: 'LAST MESSAGE TIME',
    cell: (info) => (
      <Typography sx={{ fontSize: { xs: '14px', lg: '16px' } }}>
        {formatDate(info?.row?.original?.updated_at)}
      </Typography>
    )
  },
  {
    header: 'NUMBER OF MESSAGES',
    cell: (info) => (
      <Typography sx={{ fontSize: { xs: '14px', lg: '16px' } }}>
        {info.row.original.interaction_journey_message_count ?? '0'}
      </Typography>
    )
  },
  {
    header: 'ACTION',
    size: 80,
    cell: (info) => (
      <Box display='flex' gap={{ xs: 0.5, lg: 0 }}>
        <IconButton component={Link} to={String(info.row.original.id)} sx={{ padding: { xs: '4px', lg: '8px' } }}>
          <MdOutlineRemoveRedEye color='#777777' size={24} />
        </IconButton>
        {!info.row.original.discarded_at && (
          <IconButton onClick={() => archiveCallLog(info.row.original.id)} sx={{ padding: { xs: '4px', lg: '8px' } }}>
            <RiInboxArchiveLine color='#777777' size={22} />
          </IconButton>
        )}
      </Box>
    )
  }
];
